/**
 * CIaO Registrations List Page
 *
 * Filter panel UI, datepicker bridging, and the registration-detail
 * dynamic modal (form-modals.mdc): the per-row "View" eye fetches
 * ``_registration_detail_body.html`` into the ``#ciao-detail-modal``
 * shell. The Retry form inside the modal is intercepted by the helper
 * and answered as JSON by ``ciao_registration_retry`` (close +
 * soft-reload of the list).
 * Filtering/pagination is handled declaratively by HTMX.
 */

import { createDynamicModal } from '../utils/ui/dynamic-modal.js';

let detailModalHelper = null;

function initDetailModal() {
  if (detailModalHelper) return;
  detailModalHelper = createDynamicModal({
    modalId: 'ciao-detail-modal',
    onSuccess: function (data, helper) {
      // Retry queued: toast the server message, then close + refresh the
      // list so the row's status chip flips to Pending.
      if (data && data.message && typeof window.showToast === 'function') {
        window.showToast(data.message, 'success');
      }
      helper.close(function () {
        if (typeof window._softReload === 'function') window._softReload();
      });
    },
  });
}

function handleDetailOpenClick(evt) {
  var t = evt.target;
  if (!t || !t.closest) return;
  var btn = t.closest('[data-ciao-detail-open]');
  if (!btn) return;
  if (!detailModalHelper) return;
  evt.preventDefault();

  var root = document.querySelector('[data-auto-init="ciaoList"]');
  var title = (root && root.dataset.ciaoDetailModalTitle) || 'CIaO Registration';
  var url = btn.dataset.ciaoDetailUrl;
  if (!url) return;
  detailModalHelper.open(url, title);
}

/**
 * Initialize the CIaO registrations list page
 */
export function initCiaoList(options = {}) {
  const { hasActiveFilters = false } = options;
  // Boosted nav replaces the modal shell node — a cached helper would
  // point at the orphan (form-modals.mdc §9). Re-create on every init.
  detailModalHelper = null;
  initDetailModal();
  initFilterPanelUI(hasActiveFilters);
  initDatepickerBridge();
  bindAfterSettleOnce();
  bindDetailDelegationOnce();
}

let detailDelegationBound = false;

function bindDetailDelegationOnce() {
  if (detailDelegationBound) return;
  detailDelegationBound = true;
  document.body.addEventListener('click', handleDetailOpenClick);
}

function initFilterPanelUI(hasActiveFilters) {
  var panel = document.getElementById('filter-panel');
  var chevron = document.getElementById('filter-chevron');
  var toggleBtn = document.getElementById('toggle-filters-btn');
  var resetBtn = document.getElementById('reset-filters-btn');
  var form = document.getElementById('filterForm');

  if (panel && hasActiveFilters) {
    panel.classList.remove('hidden');
    if (chevron) chevron.classList.add('rotate-180');
  }

  // Per-node idempotency flag via plain property — does not serialize
  // into HTMX hx-boost history snapshots.
  if (toggleBtn && panel && !toggleBtn._ciFilterBound) {
    toggleBtn._ciFilterBound = true;
    toggleBtn.addEventListener('click', function () {
      panel.classList.toggle('hidden');
      if (chevron) chevron.classList.toggle('rotate-180');
    });
  }

  if (resetBtn && form && !resetBtn._ciResetBound) {
    resetBtn._ciResetBound = true;
    resetBtn.addEventListener('click', function () {
      form.querySelectorAll('select').forEach(function (sel) {
        sel.value = '';
        var wrapper = sel.closest('.custom-select-wrapper');
        if (wrapper) {
          var textEl = wrapper.querySelector('.custom-select-text');
          if (textEl && sel.options[0]) {
            textEl.textContent = sel.options[0].text;
          }
        }
      });

      form.querySelectorAll('input[name="date_from"], input[name="date_to"]').forEach(function (input) {
        input.value = '';
      });

      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }
}

/**
 * Bridge Flowbite datepicker's changeDate event to a native change event
 * so HTMX's hx-trigger can pick it up.
 */
function initDatepickerBridge() {
  var form = document.getElementById('filterForm');
  if (!form) return;

  form.querySelectorAll('input[datepicker]').forEach(function (input) {
    if (input._ciDpBound) return;
    input._ciDpBound = true;
    input.addEventListener('changeDate', function () {
      this.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

let afterSettleBound = false;

function bindAfterSettleOnce() {
  if (afterSettleBound) return;
  afterSettleBound = true;
  document.addEventListener('htmx:afterSettle', function (e) {
    var target = e.detail && e.detail.target;
    if (!target || target.id !== 'list-results') return;
    if (typeof window.reinitFlowbiteWidgets === 'function') {
      window.reinitFlowbiteWidgets(target);
    }
  });
}

/**
 * Auto-init entry point (wired via ``data-auto-init="ciaoList"``).
 */
export function autoInitCiaoList() {
  const root = document.querySelector('[data-auto-init="ciaoList"]');
  if (!root) return;
  initCiaoList({
    hasActiveFilters: root.dataset.hasActiveFilters === '1',
  });
}

window.initCiaoList = initCiaoList;
